import { useEffect, useMemo, useState } from 'react';
import { REPORTS } from '../data/reports';
import { fetchPublishedIntelligence } from '../services/intelligenceService';

const MONTH_INDEX = {
  JAN: 0,
  FEB: 1,
  MAR: 2,
  APR: 3,
  MAY: 4,
  JUN: 5,
  JUL: 6,
  AUG: 7,
  SEP: 8,
  OCT: 9,
  NOV: 10,
  DEC: 11,
};

const getReportTimestamp = (report) => {
  const rawDate = report.publishedAt || report.published_at || report.date;
  const monthYearMatch = typeof rawDate === 'string' && rawDate.match(/^([A-Z]{3})\s+(\d{4})$/);

  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return new Date(Number(year), MONTH_INDEX[month] ?? 0, 1).getTime();
  }

  const timestamp = rawDate ? new Date(rawDate).getTime() : 0;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const useIntelligence = () => {
  const [remoteItems, setRemoteItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadItems = async () => {
      setIsLoading(true);
      const result = await fetchPublishedIntelligence();

      if (!isMounted) return;

      if (result.success) {
        setRemoteItems(result.data);
        setError('');
      } else {
        console.warn('Supabase intelligence could not be loaded; using static archive fallback.', result.error);
        setError(result.error);
      }

      setIsLoading(false);
    };

    loadItems();

    return () => {
      isMounted = false;
    };
  }, []);

  const reports = useMemo(() => {
    const remoteSlugs = new Set(remoteItems.map((item) => item.id));
    const localFallbacks = REPORTS.filter((report) => !remoteSlugs.has(report.id));
    return [...remoteItems, ...localFallbacks].sort((a, b) => getReportTimestamp(b) - getReportTimestamp(a));
  }, [remoteItems]);

  return {
    reports,
    isLoading,
    error,
  };
};
